/**
 * Inventory Suite navigation — single source of truth for all three module sidebars.
 *
 * Paths are relative to the module base, which is itself relative to the suite base
 * (`/inventory-suite`). `path: ''` marks the module Overview.
 *
 * Item shape: { id, label, icon, path, match?, badgeKey?, disabled? }
 *   match     extra path prefixes this item owns. Needed because create routes use a
 *             singular segment while lists use the plural (`/requisitions` vs
 *             `/requisition/new`, `/pos` vs `/po/new`).
 *   badgeKey  key into the badges object the layout passes down; no key = no badge.
 */
import { INV_ICONS } from './constants/icons';
import { INV_ACTIONS, INV_SUITE_BASE } from './utils/inventoryRoles';

/** Flip to true when the pre-auth backend is ready. Also gates the routes in PharmacyShell. */
export const PRE_AUTH_TAB_ENABLED = false;

const SWITCH_TO = {
  stock: { id: 'go-stock', label: 'Inventory Management', icon: INV_ICONS.inventory, module: 'stock' },
  material: { id: 'go-material', label: 'Material Management', icon: INV_ICONS.material, module: 'material' },
  pharmacy: { id: 'go-pharmacy', label: 'Pharmacy Management', icon: INV_ICONS.pharmacy, module: 'pharmacy' },
};

const IPD_HOME = { id: 'go-home', label: 'IPD Home', icon: INV_ICONS.hospital, href: '/main-landing' };

export const NAV_MODULES = {
  stock: {
    key: 'stock',
    base: '/stock',
    title: 'Inventory Management',
    subtitle: 'Store & stock operations',
    icon: INV_ICONS.inventory,
    groups: [
      {
        id: 'home',
        label: null,
        items: [
          { id: 'stock-overview', label: 'Overview', icon: INV_ICONS.overview, path: '' },
        ],
      },
      {
        id: 'operations',
        label: 'Operations',
        items: [
          { id: 'queues', label: 'Store Queues', icon: INV_ICONS.storeQueues, path: '/queues' },
          { id: 'requisitions', label: 'Requisitions', icon: INV_ICONS.requisitions, path: '/requisitions', match: ['/requisition'] },
          { id: 'issues', label: 'Issues', icon: INV_ICONS.issues, path: '/issues' },
          { id: 'returns', label: 'Returns', icon: INV_ICONS.returns, path: '/returns', match: ['/return'] },
          { id: 'consumptions', label: 'Consumptions', icon: INV_ICONS.consumptions, path: '/consumptions', match: ['/consumption'] },
          { id: 'consolidations', label: 'Consolidations', icon: INV_ICONS.consolidations, path: '/consolidations' },
        ],
      },
      {
        id: 'stock-control',
        label: 'Stock control',
        items: [
          { id: 'stores', label: 'Stores', icon: INV_ICONS.stores, path: '/stores' },
          { id: 'balances', label: 'Stock Balances', icon: INV_ICONS.balances, path: '/balances' },
          { id: 'opening-stock', label: 'Opening Stock', icon: INV_ICONS.openingStock, path: '/opening-stocks', match: ['/opening-stock'] },
          { id: 'transfer', label: 'Transfers', icon: INV_ICONS.transfer, path: '/transfers', match: ['/transfer'] },
          { id: 'adjustments', label: 'Stock Adjustments', icon: INV_ICONS.adjustments, path: '/adjustments', match: ['/adjustment'] },
          { id: 'goods-discard', label: 'Goods Discard', icon: INV_ICONS.goodsDiscard, path: '/goods-discard' },
          { id: 'gate-passes', label: 'Gate Pass', icon: INV_ICONS.gatePass, path: '/gate-passes', match: ['/gate-pass'] },
        ],
      },
      {
        id: 'trace',
        label: 'Trace & audit',
        items: [
          { id: 'ledger', label: 'Ledger', icon: INV_ICONS.ledger, path: '/ledger' },
          // { id: 'batch-trace', label: 'Batch Trace', icon: INV_ICONS.batchTrace, path: '/batch-trace' },
        ],
      },
    ],
    footer: [SWITCH_TO.material, SWITCH_TO.pharmacy, IPD_HOME],
  },

  material: {
    key: 'material',
    base: '/material',
    title: 'Material Management',
    subtitle: 'Masters & procurement',
    icon: INV_ICONS.material,
    groups: [
      {
        id: 'home',
        label: null,
        items: [
          { id: 'material-overview', label: 'Overview', icon: INV_ICONS.overview, path: '' },
        ],
      },
      {
        id: 'masters',
        label: 'Master data',
        items: [
          { id: 'material-groups', label: 'Material Groups', icon: INV_ICONS.materialGroups, path: '/material-groups' },
          { id: 'item-classes', label: 'Item Classes', icon: INV_ICONS.itemClasses, path: '/item-classes' },
          { id: 'categories', label: 'Categories', icon: INV_ICONS.categories, path: '/categories' },
          { id: 'uoms', label: 'UOMs', icon: INV_ICONS.uoms, path: '/uoms' },
          { id: 'tax-masters', label: 'Tax Master', icon: INV_ICONS.taxMaster, path: '/tax-masters' },
          { id: 'items', label: 'Items', icon: INV_ICONS.items, path: '/items' },
          { id: 'vendors', label: 'Vendors', icon: INV_ICONS.vendors, path: '/vendors' },
        ],
      },
      {
        id: 'procurement',
        label: 'Procurement',
        items: [
          { id: 'rfqs', label: 'RFQs', icon: INV_ICONS.rfqs, path: '/rfqs', match: ['/rfq'] },
          { id: 'work-orders', label: 'Work Orders', icon: INV_ICONS.workOrders, path: '/work-orders', match: ['/work-order'] },
          { id: 'pos', label: 'Purchase Orders', icon: INV_ICONS.purchaseOrders, path: '/pos', match: ['/po'] },
          { id: 'grns', label: 'GRNs', icon: INV_ICONS.grns, path: '/grns', match: ['/grn'] },
          { id: 'rejected-items', label: 'Rejected Items', icon: INV_ICONS.rejectedItems, path: '/rejected-items' },
          { id: 'vendor-returns', label: 'Vendor Returns', icon: INV_ICONS.vendorReturns, path: '/vendor-returns', match: ['/vendor-return'] },
          { id: 'purchase-invoices', label: 'Purchase Invoices', icon: INV_ICONS.purchaseInvoices, path: '/purchase-invoices', match: ['/purchase-invoice'] },
        ],
      },
    ],
    footer: [SWITCH_TO.stock, SWITCH_TO.pharmacy, IPD_HOME],
  },

  pharmacy: {
    key: 'pharmacy',
    base: '/pharmacy',
    title: 'Pharmacy Management',
    subtitle: 'Dispensing & billing',
    icon: INV_ICONS.pharmacy,
    // Every pharmacy screen sits behind one route guard on this permission (the
    // PharmacyShell route in InventoryApp), because the module's own 35 action
    // permissions are not wired this cycle (`rbac spec.md` §5). So unlike stock and
    // material, losing the overview permission leaves nothing else to land on —
    // moduleLandingPath() reads this and gives up instead of offering a screen that
    // the shell guard would bounce right back.
    entryAction: INV_ACTIONS.PHARMACY_OVERVIEW_VIEW,
    groups: [
      {
        id: 'home',
        label: null,
        items: [
          { id: 'pharmacy-overview', label: 'Overview', icon: INV_ICONS.overview, path: '' },
        ],
      },
      {
        id: 'dispensing',
        label: 'Dispensing',
        items: [
          {
            id: 'active-dispenses',
            label: 'IPD Dispense',
            icon: INV_ICONS.activeDispenses,
            path: '/active-dispenses',
            match: ['/start-dispense', '/dispenses', '/dispense'],
            badgeKey: 'active',
          },
          { id: 'controlled-queue', label: 'Controlled Queue', icon: INV_ICONS.controlledQueue, path: '/controlled-queue', badgeKey: 'controlled' },
          {
            id: 'controlled-orders',
            label: 'Controlled Orders',
            icon: INV_ICONS.controlledQueue,
            path: '/controlled-orders',
            badgeKey: 'controlledOrders',
          },
          {
            id: 'direct-dispenses',
            label: 'Direct Dispenses',
            icon: INV_ICONS.directDispenses,
            path: '/direct-dispenses',
            match: ['/opd-dispenses'],
          },
        ],
      },
      {
        id: 'billing',
        label: 'Billing & returns',
        items: [
          { id: 'bills', label: 'Bills', icon: INV_ICONS.bills, path: '/bills', match: ['/billing'] },
          { id: 'refunds', label: 'Refunds', icon: INV_ICONS.refunds, path: '/refunds', badgeKey: 'refunds' },
          { id: 'ward-returns', label: 'Ward Returns', icon: INV_ICONS.wardReturns, path: '/ward-returns', match: ['/return'], badgeKey: 'returns' },
          {
            id: 'pre-auth',
            label: 'Pre Auth',
            icon: INV_ICONS.preAuth,
            path: '/pre-auth',
            badgeKey: 'preauth',
            disabled: !PRE_AUTH_TAB_ENABLED,
          },
        ],
      },
    ],
    footer: [SWITCH_TO.stock, SWITCH_TO.material, IPD_HOME],
  },
};

/**
 * Nav item id → the action guarding the route that item's `path` points at.
 *
 * Note this is the route's action, not the domain's view action: `consolidations`
 * points at the generate form, and `transfer` maps to a create permission because
 * RBAC defines no `transfer view`. Pharmacy ids are deliberately unmapped — that
 * module is out of scope (`rbac spec.md` §5) and unmapped items are always shown.
 */
const NAV_ITEM_ACTIONS = {
  // Inventory Management
  'stock-overview': INV_ACTIONS.STOCK_OVERVIEW_VIEW,
  queues: INV_ACTIONS.QUEUES_VIEW,
  requisitions: INV_ACTIONS.REQUISITION_VIEW,
  issues: INV_ACTIONS.ISSUE_VIEW,
  returns: INV_ACTIONS.RETURN_VIEW,
  consumptions: INV_ACTIONS.CONSUMPTION_VIEW,
  consolidations: INV_ACTIONS.CONSOLIDATION_CREATE,
  stores: INV_ACTIONS.STORE_VIEW,
  balances: INV_ACTIONS.BALANCE_VIEW,
  'opening-stock': INV_ACTIONS.OPENING_STOCK_VIEW,
  transfer: INV_ACTIONS.TRANSFER_CREATE,
  adjustments: INV_ACTIONS.ADJUSTMENT_VIEW,
  'goods-discard': INV_ACTIONS.DISCARD_VIEW,
  'gate-passes': INV_ACTIONS.GATEPASS_VIEW,
  ledger: INV_ACTIONS.LEDGER_VIEW,
  'batch-trace': INV_ACTIONS.BATCH_TRACE_VIEW,

  // Material Management
  'material-overview': INV_ACTIONS.MATERIAL_OVERVIEW_VIEW,
  'material-groups': INV_ACTIONS.MASTER_MANAGE,
  categories: INV_ACTIONS.MASTER_MANAGE,
  'item-classes': INV_ACTIONS.MASTER_MANAGE,
  uoms: INV_ACTIONS.MASTER_MANAGE,
  'tax-masters': INV_ACTIONS.MASTER_MANAGE,
  items: INV_ACTIONS.ITEM_MANAGE,
  vendors: INV_ACTIONS.VENDOR_VIEW,
  rfqs: INV_ACTIONS.RFQ_VIEW,
  'work-orders': INV_ACTIONS.RFQ_VIEW,
  pos: INV_ACTIONS.PO_VIEW,
  grns: INV_ACTIONS.GRN_VIEW,
  'rejected-items': INV_ACTIONS.REJECTED_VIEW,
  'vendor-returns': INV_ACTIONS.VENDOR_RETURN_VIEW,
  'purchase-invoices': INV_ACTIONS.PURCHASE_INVOICE_VIEW,
};

/**
 * Drop nav items this role cannot open, then any group left with no items — a link
 * that only bounces the user back with a toast is worse than no link.
 */
export function filterNavForRole(navConfig, can, modules) {
  if (!navConfig) return navConfig;

  const groups = navConfig.groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        const action = NAV_ITEM_ACTIONS[item.id];
        return !action || can(action);
      }),
    }))
    .filter((group) => group.items.length > 0);

  // Footer holds the module switchers. SWITCH_TO entries carry `module`; IPD_HOME has
  // none and is always kept. Offering a switch to a workspace the user has no
  // entitlement for would just bounce them straight back — and so would one whose
  // every screen is denied, which is what moduleLandingPath() answers.
  const footer = (navConfig.footer || []).filter(
    (item) => !item.module || Boolean(modules?.[item.module] && moduleLandingPath(item.module, can)),
  );

  return { ...navConfig, groups, footer };
}

/**
 * Where entering `moduleKey` should land this user: the first sidebar item they can
 * open, in sidebar order. Null when the module holds nothing for them.
 *
 * The module Overview is only the first candidate, not a prerequisite — holding the
 * workspace entitlement without its overview permission lands the user on whatever
 * they do hold (e.g. Consumptions) rather than locking them out of a workspace they
 * were granted. Order follows the sidebar, so the landing screen is always the
 * top-most link they can see.
 *
 * Ordering is the same reason this lives here and not in utils/inventoryRoles.js:
 * the answer is a property of the nav config, and that module must not import this
 * one back.
 */
export function moduleLandingPath(moduleKey, can) {
  const navConfig = NAV_MODULES[moduleKey];
  if (!navConfig) return null;
  if (navConfig.entryAction && !can(navConfig.entryAction)) return null;

  for (const group of navConfig.groups) {
    for (const item of group.items) {
      if (item.disabled) continue;
      const action = NAV_ITEM_ACTIONS[item.id];
      if (!action || can(action)) return `${INV_SUITE_BASE}${navConfig.base}${item.path}`;
    }
  }

  return null;
}

/**
 * Where to send someone who hit a screen they cannot open. Picks the first module
 * they can both enter (module gate) and land in (at least one grantable screen) — a
 * gate with nothing behind it would leave them on a blank shell.
 *
 * No module at all means no inventory access, so they must leave the suite the way
 * the sidebar's IPD Home link does — returning an in-suite path would redirect-loop.
 */
export function invHomePath(modules, can) {
  for (const key of ['stock', 'material', 'pharmacy']) {
    if (!modules?.[key]) continue;
    const landing = moduleLandingPath(key, can);
    if (landing) return landing;
  }

  return IPD_HOME.href;
}

/** Which module workspace a suite-relative pathname belongs to. Suite root → stock. */
export function resolveModuleKey(pathname, suiteBase) {
  const rest = pathname?.startsWith(suiteBase) ? pathname.slice(suiteBase.length) : '';
  const seg = rest.split('/').filter(Boolean)[0];
  return NAV_MODULES[seg] ? seg : 'stock';
}

const isUnder = (pathname, prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`);
const depth = (rel) => rel.split('/').filter(Boolean).length;

/**
 * The id of the single nav item that owns `pathname`, or null.
 *
 * A plain startsWith would light up several items at once here: `/stock` prefixes
 * everything, and `/material/pos` vs `/material/po/new` are siblings. So:
 *   - the Overview item (path '') matches only on an exact hit at the module base;
 *   - every other item owns its own subtree, bounded at a path segment, plus `match`;
 *   - ties go to the deepest matching prefix.
 * The caller resolves once and compares ids, so two items can never both be active.
 */
export function resolveActiveNavId(navConfig, moduleBase, pathname) {
  if (!navConfig || !pathname) return null;
  const clean = pathname.replace(/\/+$/, '') || pathname;
  let best = null;

  navConfig.groups.forEach((group) => {
    group.items.forEach((item) => {
      [item.path, ...(item.match || [])].forEach((rel) => {
        const abs = `${moduleBase}${rel}`;
        const hit = rel === '' ? clean === abs : isUnder(clean, abs);
        if (!hit) return;
        const score = depth(rel);
        if (!best || score > best.score) best = { id: item.id, score };
      });
    });
  });

  return best ? best.id : null;
}

/** Look up an item by id across a config's groups. */
export function findNavItem(navConfig, id) {
  if (!navConfig || !id) return null;
  for (const group of navConfig.groups) {
    const hit = group.items.find((item) => item.id === id);
    if (hit) return hit;
  }
  return null;
}
